import { isPlaceholderIdentity } from '@evi/shared';
import { viewForConnectorStatus, type ConnectorStatusView } from './connectorStatus';

/**
 * What a stall row says, for the one stall list the map's location sheet
 * and the station page share (Warren, 2026-10-23: "Show each charger's
 * name (e.g. 'Visitor Spot') as the main label, not the model. Model and
 * power can sit underneath, small.").
 *
 * Pure so it can be tested without React. Every value comes from the
 * charger record the Api sent: nothing here guesses a power or a model.
 */

export interface StallFacts {
  identity: string;
  display_name?: string | null;
  vendor?: string | null;
  model?: string | null;
  is_online: boolean;
  connectors?: ReadonlyArray<PlugFacts> | null;
  maintenance_windows?: ReadonlyArray<{ start: string; end: string }> | null;
  /** The charger's access resolved against THIS driver ('tenant',
   *  'guest', 'granted', 'guest_closed'). Absent from an older Api. */
  access_mode?: string | null;
  /** Item 6: a resident's charger with no guest price turns this driver
   *  away as a paying guest. */
  guest_price_missing?: boolean | null;
}

export interface PlugFacts {
  connector_id: number;
  type?: string | null;
  max_power_kw?: number | null;
  status?: string;
}

/** The row's main label: the operator's name for the stall, else its
 *  OCPP identity (always present, so a row is never blank). */
export function stallName(c: Pick<StallFacts, 'identity' | 'display_name'>): string {
  const name = typeof c.display_name === 'string' ? c.display_name.trim() : '';
  return name || c.identity;
}

/** "Autel MaxiCharger": vendor then model, the vendor said once when the
 *  model already starts with it. Firmware placeholders (WWWW,
 *  SingleSocketCharger) carry no information and are left out, so a unit
 *  that never named itself shows no model rather than junk. */
export function stallModel(c: Pick<StallFacts, 'vendor' | 'model'>): string {
  const vendor = isPlaceholderIdentity(c.vendor) ? '' : String(c.vendor).trim();
  const model = isPlaceholderIdentity(c.model) ? '' : String(c.model).trim();
  if (!model) return vendor;
  if (!vendor || model.toLowerCase().startsWith(vendor.toLowerCase())) return model;
  return `${vendor} ${model}`;
}

/** The stored connector power, or null when it is missing or not a
 *  positive number. Rounded to one decimal so 7.68 reads 7.7. */
export function plugKw(plug: Pick<PlugFacts, 'max_power_kw'> | null | undefined): number | null {
  const kw = plug?.max_power_kw;
  if (typeof kw !== 'number' || !Number.isFinite(kw) || kw <= 0) return null;
  return Math.round(kw * 10) / 10;
}

/** "J1772 · 7 kW" for one plug. `kwLabel` localizes the unit. */
export function plugSpec(
  plug: Pick<PlugFacts, 'type' | 'max_power_kw'> | null | undefined,
  kwLabel: (kw: number) => string,
): string {
  const type = typeof plug?.type === 'string' ? plug.type.trim() : '';
  const kw = plugKw(plug);
  return [type, kw === null ? '' : kwLabel(kw)].filter(Boolean).join(' · ');
}

/** The small line under a single-plug stall's name: model, plug type and
 *  power, whichever are known. */
export function stallDetail(c: StallFacts, kwLabel: (kw: number) => string): string {
  return [stallModel(c), plugSpec(c.connectors?.[0], kwLabel)].filter(Boolean).join(' · ');
}

/** Two or more physical plugs on one OCPP charger. The public payload
 *  lists each as its own connectors[] entry with its own live status. */
export function isMultiPlug(c: Pick<StallFacts, 'connectors'>): boolean {
  return (c.connectors?.length ?? 0) > 1;
}

/** True while any of the charger's maintenance windows covers `now`. */
export function inMaintenance(
  c: Pick<StallFacts, 'maintenance_windows'>,
  now: Date = new Date(),
): boolean {
  return (c.maintenance_windows ?? []).some((w) => new Date(w.start) <= now && now <= new Date(w.end));
}

/** A plug's pill and whether its Start button is live. A maintenance
 *  window beats every connector status, as on the map pin and the old
 *  sheet's Maint pill; otherwise the canonical connector mapping decides
 *  (offline wins, Available and Plugged in start). */
export type StallStatusView =
  | ConnectorStatusView
  | { key: 'maintenance'; label: 'Maintenance'; tone: 'power'; selectable: false };

export function stallStatus(
  status: string | undefined,
  isOnline: boolean,
  maintenance: boolean,
): StallStatusView {
  if (maintenance) return { key: 'maintenance', label: 'Maintenance', tone: 'power', selectable: false };
  return viewForConnectorStatus(status, isOnline);
}

/** A cross-tenant charger the map shows only in public mode: the
 *  operator has not opened it to guests, so a start is refused (the map
 *  pin greys it as "not available to your account"). */
export function isGuestClosed(c: Pick<StallFacts, 'access_mode'>): boolean {
  return c.access_mode === 'guest_closed';
}

/** Whether a stall's (or a plug's) Start button is live. The status view
 *  decides first (offline, maintenance, busy plugs); a guest-closed
 *  charger never starts, whatever its connector says, so the button is
 *  off rather than a tap that bounces the driver. */
export function canStart(
  c: Pick<StallFacts, 'access_mode'>,
  view: Pick<StallStatusView, 'selectable'>,
): boolean {
  return view.selectable && !isGuestClosed(c);
}

/** Whether the row carries "This charger isn't open to guests yet": a
 *  guest-closed charger, or a resident's charger with no guest price. */
export function notOpenToGuests(c: Pick<StallFacts, 'access_mode' | 'guest_price_missing'>): boolean {
  return c.guest_price_missing === true || isGuestClosed(c);
}

/** Where a Start began, carried as ?from= so the pre-session page's Back
 *  returns there. Only the map's location sheet sets one. */
export type StartOrigin = 'map';

/** The pre-session route a Start button opens. ?plug= is written only for
 *  a multi-plug charger, so a single-plug stall opens exactly the route
 *  it always has (and the one the QR stickers carry); PreSessionPage reads
 *  a missing plug as the charger's first connector. `from` adds
 *  ?from=map (after any ?plug=) for a start begun on the map's sheet. */
export function stallStartPath(
  locationId: string,
  c: Pick<StallFacts, 'identity' | 'connectors'>,
  connectorId: number,
  from?: StartOrigin,
): string {
  const params = new URLSearchParams();
  if (isMultiPlug(c)) params.set('plug', String(connectorId));
  if (from) params.set('from', from);
  const query = params.toString();
  return `/stations/${locationId}/stall/${c.identity}${query ? `?${query}` : ''}`;
}

/** Where the pre-session page's Back goes. A start begun on the map's
 *  sheet (?from=map) returns to the map, where the driver chose the
 *  stall, rather than to a station page repeating that sheet; a QR
 *  sticker or a deep link carries no origin and keeps the station page. */
export function preSessionBackPath(search: string, locationId: string): string {
  return new URLSearchParams(search).get('from') === 'map' ? '/map' : `/stations/${locationId}`;
}
